// TraitementDivers.jsx (RH/Admin) — Redesign Premium matching other modules
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Search, Filter, Check, X, User, AlertCircle,
    FileText, ChevronDown, Download, MessageSquare, Loader, Eye, Clock,
} from "lucide-react";

import {
    getAdminAttestationInboxRequest,
    adminAttestationDecisionRequest,
    downloadAttestationRequestPdfRequest,
    clearAttestationsMessage,
} from "../../../../redux/actions/attestationsActions";

import "../TraitementStyles/TraitementNotesFrais.css";

const TraitementDivers = () => {
    const dispatch = useDispatch();

    /* ── Redux ──────────────────────────────────────────────── */
    const attState = useSelector((state) => state.attestations || {});
    const adminInbox = attState.adminInbox || [];
    const loading = !!attState.loading;
    const apiError = attState.error;
    const apiMessage = attState.message;

    /* ── Local state ─────────────────────────────────────────── */
    const [activeTab, setActiveTab] = useState("enCours");
    const [searchTerm, setSearchTerm] = useState("");
    const [showFilters, setShowFilters] = useState(false);

    const [showRejectModal, setShowRejectModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedDemande, setSelectedDemande] = useState(null);
    const [motifRejet, setMotifRejet] = useState("");

    const [filters, setFilters] = useState({
        typeAttestation: "",
        departement: "",
        dateDebut: "",
        dateFin: "",
    });

    useEffect(() => {
        dispatch(getAdminAttestationInboxRequest());
    }, [dispatch]);

    useEffect(() => {
        if (apiError || apiMessage) {
            const t = setTimeout(() => dispatch(clearAttestationsMessage()), 1500);
            return () => clearTimeout(t);
        }
    }, [apiError, apiMessage, dispatch]);

    /* ── Helpers ─────────────────────────────────────────────── */
    const formatDate = (d) => {
        if (!d) return "—";
        const dt = new Date(d);
        if (Number.isNaN(dt.getTime())) return d;
        return dt.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
    };

    const mapStatusToTab = (s) => {
        const v = String(s || "").toUpperCase();
        if (v === "APPROVED") return "approuve";
        if (v === "REJECTED") return "rejete";
        return "enCours";
    };

    const demandes = useMemo(() => {
        return (adminInbox || []).map((d) => {
            const att = d.attestation || {};
            const statut = mapStatusToTab(d.status || d.statut);
            return {
                id: d.id,
                matricule: d.matricule || d.code || "—",
                collaborateur: d.fullName || d.nomComplet || `${d.employeeNom || ""} ${d.employeePrenom || ""}`.trim() || "—",
                poste: d.poste || d.jobTitle || d.posteNom || "—",
                departement: d.departement || d.department || d.departementNom || "—",
                typeAttestation: d.attestationTitre || att.titre || att.nom || "Attestation",
                couleurType: d.couleurType || att.couleur || "#3b82f6",
                dateDemande: d.createdAt || d.dateDemande || "",
                motif: d.motif || d.commentaire || "",
                attachments: d.attachments || d.documentsJoints || [],
                managerComment: d.managerComment || "",
                adminComment: d.adminComment || "",
                statut,
                rawStatus: d.status || d.statut,
                canDownload: statut === "approuve",
            };
        });
    }, [adminInbox]);

    const getStatutBadge = (statut) => {
        const config = {
            enCours: { label: "En attente RH", bg: "#fff3cd", color: "#856404" },
            approuve: { label: "Approuvé", bg: "#d4edda", color: "#155724" },
            rejete: { label: "Rejeté", bg: "#f8d7da", color: "#721c24" },
        };
        const c = config[statut] || config.enCours;
        return (
            <span className="tnfBadge" style={{ backgroundColor: c.bg, color: c.color }}>
                {c.label}
            </span>
        );
    };

    const filtered = useMemo(() => {
        let list = demandes.filter((d) => d.statut === activeTab);

        if (searchTerm) {
            const s = searchTerm.toLowerCase();
            list = list.filter((d) =>
                [d.collaborateur, d.matricule, d.departement, d.poste, d.typeAttestation].join(" ").toLowerCase().includes(s)
            );
        }

        if (filters.typeAttestation) list = list.filter(d => d.typeAttestation === filters.typeAttestation);
        if (filters.departement) list = list.filter(d => d.departement === filters.departement);
        if (filters.dateDebut) list = list.filter(d => String(d.dateDemande).slice(0, 10) >= filters.dateDebut);
        if (filters.dateFin) list = list.filter(d => String(d.dateDemande).slice(0, 10) <= filters.dateFin);

        return list;
    }, [demandes, activeTab, searchTerm, filters]);

    const tabs = useMemo(() => [
        { id: "enCours", label: "En cours", count: demandes.filter(d => d.statut === "enCours").length },
        { id: "approuve", label: "Approuvées", count: demandes.filter(d => d.statut === "approuve").length },
        { id: "rejete", label: "Rejetées", count: demandes.filter(d => d.statut === "rejete").length },
    ], [demandes]);

    const typesAttestation = useMemo(() => [...new Set(demandes.map(d => d.typeAttestation).filter(Boolean))], [demandes]);
    const departements = useMemo(() => [...new Set(demandes.map(d => d.departement).filter(Boolean))], [demandes]);

    /* ── Actions ─────────────────────────────────────────────── */
    const handleApprove = (id) => dispatch(adminAttestationDecisionRequest({ id, approved: true, comment: "" }));
    const handleRejectClick = (d) => { setSelectedDemande(d); setMotifRejet(""); setShowRejectModal(true); };
    const confirmReject = () => {
        if (!selectedDemande || !motifRejet.trim()) return;
        dispatch(adminAttestationDecisionRequest({ id: selectedDemande.id, approved: false, comment: motifRejet }));
        setShowRejectModal(false);
    };

    const handleDownload = (d) => dispatch(downloadAttestationRequestPdfRequest({ requestId: d.id, filename: `${d.typeAttestation}.pdf` }));

    /* ── Render ──────────────────────────────────────────────── */
    if (loading && demandes.length === 0) {
        return (
            <div className="tnfPage">
                <div className="tnfWrap" style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
                    <Loader size={32} className="spin" />
                </div>
            </div>
        );
    }

    return (
        <div className="tnfPage">
            <div className="tnfWrap">
                {/* Header */}
                <div className="tnfHeader">
                    <div>
                        <h1>Traitement des demandes d'attestations</h1>
                        <p>Gérez et validez les documents administratifs de vos collaborateurs</p>
                    </div>
                </div>

                {/* Search + Filters */}
                <div className="tnfCard">
                    <div className="tnfControls">
                        <div className="tnfSearch">
                            <Search size={18} />
                            <input
                                type="text"
                                placeholder="Rechercher par matricule, nom, type..."
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <button type="button" className="tnfFilterBtn" onClick={() => setShowFilters(v => !v)}>
                            <Filter size={16} /> Filtres
                            <ChevronDown size={16} className={`tnfChevron ${showFilters ? "is-open" : ""}`} />
                        </button>
                    </div>

                    {showFilters && (
                        <div className="tnfFilters">
                            <div className="tnfField">
                                <label>Type d'attestation</label>
                                <select value={filters.typeAttestation} onChange={e => setFilters(p => ({ ...p, typeAttestation: e.target.value }))}>
                                    <option value="">Tous</option>
                                    {typesAttestation.map(t => <option key={t} value={t}>{t}</option>)}
                                </select>
                            </div>
                            <div className="tnfField">
                                <label>Département</label>
                                <select value={filters.departement} onChange={e => setFilters(p => ({ ...p, departement: e.target.value }))}>
                                    <option value="">Tous</option>
                                    {departements.map(d => <option key={d} value={d}>{d}</option>)}
                                </select>
                            </div>
                            <div className="tnfField"><label>Après le</label><input type="date" value={filters.dateDebut} onChange={e => setFilters(p => ({ ...p, dateDebut: e.target.value }))} /></div>
                            <div className="tnfField"><label>Avant le</label><input type="date" value={filters.dateFin} onChange={e => setFilters(p => ({ ...p, dateFin: e.target.value }))} /></div>
                        </div>
                    )}
                </div>

                {/* Tabs + Table */}
                <div className="tnfCard tnfCard--noPad">
                    <div className="tnfTabs">
                        {tabs.map(tab => (
                            <button key={tab.id} type="button"
                                className={`tnfTab ${activeTab === tab.id ? "is-active" : ""}`}
                                onClick={() => setActiveTab(tab.id)}>
                                {tab.label}
                                <span className={`tnfCount ${activeTab === tab.id ? "is-active" : ""}`}>{tab.count}</span>
                            </button>
                        ))}
                    </div>

                    <div className="tnfTableWrap">
                        {filtered.length === 0 ? (
                            <div className="tnfEmpty"><AlertCircle size={46} /><div>Aucune demande trouvée</div></div>
                        ) : (
                            <table className="tnfTable">
                                <thead>
                                    <tr>
                                        <th>Matricule</th>
                                        <th>Collaborateur</th>
                                        <th>Département</th>
                                        <th>Type d'attestation</th>
                                        <th>Date de la demande</th>
                                        <th>Statut</th>
                                        <th className="is-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.map(d => (
                                        <tr key={d.id} onClick={() => { setSelectedDemande(d); setShowDetailsModal(true); }} style={{ cursor: "pointer" }}>
                                            <td><span className="tnfPrimary">{d.matricule}</span></td>
                                            <td>
                                                <div className="tnfPrimary">{d.collaborateur}</div>
                                                <div className="tnfSub">{d.poste}</div>
                                            </td>
                                            <td><span className="tnfMuted">{d.departement}</span></td>
                                            <td>
                                                <div className="tnfType">
                                                    <span className="tnfDot" style={{ backgroundColor: d.couleurType }} />
                                                    <span className="tnfPrimary">{d.typeAttestation}</span>
                                                </div>
                                            </td>
                                            <td><div className="tnfDate"><Clock size={14} />{formatDate(d.dateDemande)}</div></td>
                                            <td>{getStatutBadge(d.statut)}</td>
                                            <td className="is-right" onClick={e => e.stopPropagation()}>
                                                <div className="tnfRowActions">
                                                    {activeTab === "enCours" ? (
                                                        <>
                                                            <button type="button" className="tnfActionBtn tnfActionBtn--ok" onClick={() => handleApprove(d.id)} title="Approuver"><Check size={16} /></button>
                                                            <button type="button" className="tnfActionBtn tnfActionBtn--no" onClick={() => handleRejectClick(d)} title="Refuser"><X size={16} /></button>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <button type="button" className="tnfDetailsBtn" onClick={() => { setSelectedDemande(d); setShowDetailsModal(true); }}>Détails</button>
                                                            {d.canDownload && <button type="button" className="tnfActionBtn tnfActionBtn--dl" onClick={() => handleDownload(d)} title="Télécharger"><Download size={16} /></button>}
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>

            {/* Details Modal */}
            {showDetailsModal && selectedDemande && (
                <div className="tnfModal">
                    <div className="tnfModal__panel" role="dialog" aria-modal="true">
                        <div className="tnfModal__header">
                            <div className="tnfModal__headLeft">
                                <div className="tnfModal__icon"><FileText size={18} /></div>
                                <div><div className="tnfModal__title">Détails de la demande</div><div className="tnfModal__sub">{selectedDemande.typeAttestation}</div></div>
                            </div>
                            <button type="button" className="tnfIconBtn" onClick={() => setShowDetailsModal(false)}><X size={18} /></button>
                        </div>
                        <div className="tnfModal__body">
                            <div className="tnfGrid2">
                                <div className="tnfPanel">
                                    <div className="tnfPanel__title">Collaborateur</div>
                                    <div className="tnfKv"><span>Matricule</span><b>{selectedDemande.matricule}</b></div>
                                    <div className="tnfKv"><span>Nom</span><b>{selectedDemande.collaborateur}</b></div>
                                    <div className="tnfKv"><span>Département</span><b>{selectedDemande.departement}</b></div>
                                </div>
                                <div className="tnfPanel">
                                    <div className="tnfPanel__title">Demande</div>
                                    <div className="tnfKv"><span>Type</span><b>{selectedDemande.typeAttestation}</b></div>
                                    <div className="tnfKv"><span>Soumise le</span><b>{formatDate(selectedDemande.dateDemande)}</b></div>
                                    <div className="tnfKv"><span>Statut</span><b>{getStatutBadge(selectedDemande.statut)}</b></div>
                                </div>
                                <div className="tnfPanel tnfPanel--full">
                                    <div className="tnfPanel__title"><MessageSquare size={16} /> Motif / Commentaire</div>
                                    <div className="tnfText">{selectedDemande.motif || "Aucun motif précisé"}</div>
                                </div>
                            </div>
                        </div>
                        <div className="tnfModal__footer">
                            <button className="tnfBtn tnfBtn--ghost" onClick={() => setShowDetailsModal(false)}>Fermer</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Reject Modal */}
            {showRejectModal && selectedDemande && (
                <div className="tnfModal">
                    <div className="tnfModal__panel tnfModal__panel--small" role="dialog" aria-modal="true">
                        <div className="tnfModal__header">
                            <div className="tnfModal__headLeft">
                                <div className="tnfModal__icon tnfModal__icon--danger"><AlertCircle size={18} /></div>
                                <div><div className="tnfModal__title">Refuser la demande</div><div className="tnfModal__sub">{selectedDemande.collaborateur}</div></div>
                            </div>
                            <button type="button" className="tnfIconBtn" onClick={() => setShowRejectModal(false)}><X size={18} /></button>
                        </div>
                        <div className="tnfModal__body">
                            <label className="tnfLabel">Motif du rejet <span className="tnfReq">*</span></label>
                            <textarea className="tnfTextarea" rows={4} value={motifRejet} onChange={e => setMotifRejet(e.target.value)} placeholder="Précisez la raison du refus..." />
                        </div>
                        <div className="tnfModal__footer">
                            <button className="tnfBtn tnfBtn--ghost" onClick={() => setShowRejectModal(false)}>Annuler</button>
                            <button className="tnfBtn tnfBtn--danger" disabled={!motifRejet.trim()} onClick={confirmReject}>Confirmer le rejet</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TraitementDivers;